<template>
  <div v-if="order" class="order-status-header">
    <h3 class="order-status-header__title">
      Заказ #{{ displayOrderId }}
    </h3>
    <div class="order-status-header__chips">
      <UiKitChip :color="statusChipColor" variant="flat" size="md">
        {{ statusText }}
      </UiKitChip>
      <UiKitChip v-if="orderTypeLabel" color="default" variant="bordered" size="md">
        {{ orderTypeLabel }}
      </UiKitChip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getStatusText, ORDER_STATUSES, isOrderPaidOrCompleted } from '@entities/order'
import type { Order } from '@entities/order'

interface Props {
  order: Order | null
}

const props = defineProps<Props>()

/**
 * Получает ID заказа для отображения (orderPosId если есть, иначе id)
 */
const displayOrderId = computed(() => {
  if (!props.order) return ''
  return props.order.orderPosId || props.order.id
})

/**
 * Короткая метка типа заказа для чипа (адрес целиком показывается в футере)
 */
const orderTypeLabel = computed(() => {
  const type = (props.order as any)?.order_type || (props.order as any)?.orderType
  switch (type) {
    case 'delivery': { return 'Доставка'
    }
    case 'takeaway':
    case 'pickup': { return 'Самовывоз'
    }
    case 'dine_in': { return 'В заведении'
    }
    default: { return ''
    }
  }
})

const statusText = computed(() => {
  const order = props.order
  if (!order) return ''

  if (order.status === ORDER_STATUSES.CONFIRMED) {
    return 'оплачен'
  }

  if (order.status === ORDER_STATUSES.COMPLETED) {
    return 'выполнен'
  }

  if (order.payment_status === 'paid' && !order.status) {
    return 'оплачен'
  }

  return getStatusText(order.status)
})

/**
 * Цвет чипа статуса: токены hui по смыслу статуса
 */
const statusChipColor = computed(() => {
  const order = props.order
  if (!order) return 'default'

  if (order.status === ORDER_STATUSES.CANCELLED) return 'danger'
  if (order.status === ORDER_STATUSES.COMPLETED || order.status === ORDER_STATUSES.DELIVERED) return 'success'
  if (order.status === ORDER_STATUSES.CONFIRMED || (order.payment_status === 'paid' && !order.status)) return 'success'
  if (order.status === ORDER_STATUSES.PENDING && !isOrderPaidOrCompleted(order)) return 'warning'

  return 'primary'
})
</script>

<style scoped lang="scss">
.order-status-header {
  @apply grid justify-items-center gap-2.5;

  &__title {
    @apply text-center text-[1.8rem] leading-tight font-bold text-hui-foreground;
  }

  &__chips {
    @apply flex flex-wrap justify-center items-center gap-2;
  }
}
</style>